import React, { useRef, useState, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import '@fullcalendar/core/main.css'
import '@fullcalendar/daygrid/main.css'
import '@fullcalendar/timegrid/main.css'
import styles from '../component-styles/calendar.module.scss'
import '../component-styles/calendar-overwrites.scss'
import {
  startOfYear,
  // startOfMonth,
  endOfMonth,
  formatForAPI,
  apiDateStringToDate,
  dateToTimeStamp,
} from '../helpers/dates'

import { cap } from '../helpers'

import {
  getCalendarAnomalyRangeAsync,
  getCalendarEventsAsync,
  getCalendarCampaignsAsync,
  getGaCampaignsAsync,
  getCalendarKpiMetrics,
  getCalendarKpis,
  getCalendarGoalMetrics,
} from '../store/actions'
import useResize from '../hooks/useResize'
import CalendarSideBar from './calendar-side-bar'
import SummaryBox from './calendar-summary-box'

export function formatKpi(
  kpi: number,
  currency: string,
  showCurrency?: boolean,
): string {
  const currencySymbol = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'GBP',
  }).formatToParts()[0].value

  if (kpi >= 1000000000) {
    return `${showCurrency ? currencySymbol || '£' : ''}${(
      kpi / 1000000000
    ).toFixed(2)}b`
  }

  if (kpi >= 1000000) {
    return `${showCurrency ? currencySymbol || '£' : ''}${(
      kpi / 1000000
    ).toFixed(2)}m`
  }

  if (kpi >= 1000) {
    return `${showCurrency ? currencySymbol || '£' : ''}${(kpi / 1000).toFixed(
      2,
    )}k`
  }

  return `${showCurrency ? currencySymbol || '£' : ''}${kpi}`
}

export function convertPercentageToStartFromHalf(percentage: number): number {
  let fromHalf = percentage
  fromHalf *= 10000
  fromHalf /= 2
  fromHalf += 5000
  fromHalf /= 10000
  return fromHalf
}

interface FillEventsParams {
  filters: any
  anomalies: any
  events: any
  campaigns: any
  kpis: any
  selectedKpi: string
  currency: string
}

export function fillEvents({
  filters,
  anomalies,
  events,
  campaigns,
  kpis,
  selectedKpi,
  currency,
}: FillEventsParams): any[] {
  const eventsForRendering: any[] = []
  if (selectedKpi !== 'None' && kpis.size > 0) {
    kpis.forEach((kpi: any, key: string): void => {
      const percentage = convertPercentageToStartFromHalf(kpi.percentage)
      eventsForRendering.push({
        title: formatKpi(kpi.metricValue, currency),
        start: new Date(key),
        backgroundColor: 'rgba(0, 125, 155, 0)',
        textColor: `rgba(0, 125, 155, ${percentage})`,
        borderColor: 'transparent',
      })
    })

    return eventsForRendering
  }

  if (filters.campaigns) {
    campaigns.forEach((campaign: any): void => {
      eventsForRendering.push({
        title: `Campaign: ${campaign.campaignTitle}`,
        start: apiDateStringToDate(campaign.startDate),
        end: apiDateStringToDate(campaign.endDate),
        backgroundColor: '#FFD70F',
        textColor: '#4a4100',
        borderColor: '#FFD70F',
        classNames: ['cal-campaign'],
      })
    })
  }

  if (filters.anomalies) {
    // nested loop should be fine because
    // there will never be many in the inner loop, [1-10] realistically
    anomalies.forEach(
      (dayAnomalies: AnomalyCalendarItem[], key: string): void => {
        dayAnomalies.forEach(({ displayName }: AnomalyCalendarItem): void => {
          eventsForRendering.push({
            title: `${cap(displayName)} Anomaly`,
            start: new Date(key),
            backgroundColor: 'transparent',
            textColor: '#E61969',
            borderColor: 'transparent',
          })
        })
      },
    )
  }

  if (filters.events) {
    // nested loop should be fine because
    // there will never be many in the inner loop, [1-10] realistically
    events.forEach((dayEvents: any[], key: string): void => {
      dayEvents.forEach(({ eventTitle }: any): void => {
        eventsForRendering.push({
          title: eventTitle,
          start: new Date(key),
          backgroundColor: 'transparent',
          textColor: '#007D9B',
          borderColor: 'transparent',
        })
      })
    })
  }

  return eventsForRendering
}

export function getNextHeight(height: number, width: number): number {
  if (width > height) {
    return height - 200
  }

  return width - 200
}

export function getCampaignsFromDate(
  date: Date,
  campaigns: Map<Date, any>,
): any[] {
  const nextCampaigns: any[] = []
  campaigns.forEach((campaign: any): void => {
    const selectedTS = dateToTimeStamp(date)
    const campaignStartTS = dateToTimeStamp(
      apiDateStringToDate(campaign.startDate),
    )
    const campaignEndTS = dateToTimeStamp(apiDateStringToDate(campaign.endDate))

    if (selectedTS < campaignStartTS) return
    if (selectedTS > campaignEndTS) return
    nextCampaigns.push(campaign)
  })

  return nextCampaigns
}

export default function Calendar(): React.ReactElement {
  let currency = ''
  const nextWidth = useResize()
  const nextHeight = useResize('height')
  const dispatch = useDispatch()
  const [day, setDay]: [any, any] = useState(null)
  const [month, setMonth]: [any, any] = useState(null)
  const [selectedKpi, setSelectedKpi]: [string, any] = useState('None')
  const [sideBarActive, setSideBarActive]: [boolean, any] = useState(false)
  const [filters, setFilters]: [any, any] = useState({
    events: true,
    anomalies: true,
    campaigns: true,
  })
  const { selected } = useSelector(
    (s: any): { selected: { currency: string } } => s.site,
  )

  if (selected) {
    const { currency: c } = selected
    currency = c
  }

  const setMonthSafe = (activeMonth: Date): void => {
    if (month === null || month.toString() !== activeMonth.toString()) {
      setMonth(activeMonth)
    }
  }

  useEffect((): void => {
    dispatch(
      getCalendarAnomalyRangeAsync({
        fromDate: formatForAPI(startOfYear()),
        toDate: formatForAPI(endOfMonth()),
      }),
    )
    dispatch(
      getCalendarCampaignsAsync({
        fromDate: formatForAPI(startOfYear()),
        toDate: formatForAPI(endOfMonth()),
      }),
    )
    dispatch(
      getGaCampaignsAsync({
        fromDate: formatForAPI(startOfYear()),
        toDate: formatForAPI(endOfMonth()),
      }),
    )
    dispatch(
      getCalendarEventsAsync({
        fromDate: formatForAPI(startOfYear()),
        toDate: formatForAPI(endOfMonth()),
      }),
    )
    dispatch(getCalendarKpiMetrics())
    dispatch(getCalendarGoalMetrics())
    // eslint-disable-next-line
  }, [])

  const {
    anomalies,
    events,
    campaigns,
    gaCampaigns,
    kpiMetrics,
    kpis,
  } = useSelector((s: any): any => s.calendar)

  useEffect((): void => {
    if (kpiMetrics !== [] && selectedKpi !== 'None') {
      dispatch(
        getCalendarKpis({
          fromDate: formatForAPI(startOfYear()),
          toDate: formatForAPI(endOfMonth()),
          metric: selectedKpi,
        }),
      )
    }
    // eslint-disable-next-line
  }, [selectedKpi, kpiMetrics])

  const ref = useRef(null)

  const toggleFilter = (filter: string): void => {
    const nextFilters = { ...filters }
    nextFilters[filter] = !nextFilters[filter]
    setFilters(nextFilters)
  }

  const selectDate = (e: any): void => {
    const nextSelectedDayEvent = events.get(e.date.toString()) || null
    const nextSelectedDayAnomaly = anomalies.get(e.date.toString()) || null
    const nextSelectedDayCampaign = getCampaignsFromDate(
      e.date.toString(),
      campaigns,
    )
    setDay({
      date: e.date,
      events: filters.events ? nextSelectedDayEvent : null,
      anomalies: filters.anomalies ? nextSelectedDayAnomaly : null,
      campaigns:
        nextSelectedDayCampaign.length > 0 ? nextSelectedDayCampaign : null,
    })
    setSideBarActive(true)
  }

  useEffect((): void => {
    if (day !== null) {
      selectDate(day)
    }
    // eslint-disable-next-line
  }, [filters, anomalies, events, kpis, selectedKpi, campaigns])

  const eventsForRendering = useMemo(
    (): any[] =>
      fillEvents({
        filters,
        anomalies,
        events,
        kpis,
        selectedKpi,
        campaigns,
        currency,
      }),
    [filters, anomalies, events, kpis, selectedKpi, campaigns, currency],
  )

  return (
    <>
      <div
        className={styles.container}
        data-type={selectedKpi === 'None' ? 'normal' : 'heatmap'}
      >
        <div
          className={
            sideBarActive
              ? styles.calendarContainer
              : styles.calendarContainerClosed
          }
        >
          <CalendarSideBar
            sideBarActive={sideBarActive}
            kpiMetrics={kpiMetrics}
            setSelectedKpi={setSelectedKpi}
            selectedKpi={selectedKpi}
            day={day}
            filters={filters}
            gaCampaigns={gaCampaigns}
            toggleFilter={toggleFilter}
            width={nextWidth}
            setSideBarActive={setSideBarActive}
            setDay={setDay}
          />
          <FullCalendar
            height={getNextHeight(nextHeight, nextWidth)}
            defaultView="dayGridMonth"
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            ref={ref}
            weekends
            events={eventsForRendering}
            dateClick={selectDate}
            header={{
              left: 'prev',
              center: 'next',
              right: 'title',
            }}
            fixedWeekCount={false}
            datesRender={(e: any): void => setMonthSafe(e.view.currentStart)}
          />
        </div>
        <CalendarSideBar
          mobile
          sideBarActive={sideBarActive}
          kpiMetrics={kpiMetrics}
          setSelectedKpi={setSelectedKpi}
          selectedKpi={selectedKpi}
          day={day}
          filters={filters}
          gaCampaigns={gaCampaigns}
          toggleFilter={toggleFilter}
          width={nextWidth}
          setSideBarActive={setSideBarActive}
          setDay={setDay}
        />
      </div>
      <SummaryBox date={month} />
    </>
  )
}

// https://codesandbox.io/s/fullcalendar-react-3xpj8
